/**
 * Magnus Genesis Input Wizard
 * Modal for selecting analysis input source
 */

import React, { useState, useEffect } from 'react';
import './MagnusGenesisWizard.css';

interface Requirement {
    requirementText: string;
}

interface Project {
    name: string;
    description: string;
    requirements?: Requirement[];
}

interface PDiagram {
    id: string;
    name: string;
    systemName?: string;
    requirements?: string[];
}

interface FMEA {
    id: string;
    projectName: string;
    item: string;
}

interface MagnusGenesisWizardProps {
    isOpen: boolean;
    onClose: () => void;
    onStart: (config: AnalysisConfig) => void;
    projects: Project[];
    pDiagrams: PDiagram[];
    fmeas: FMEA[];
}

export interface AnalysisConfig {
    inputSource: 'project' | 'pdiagram' | 'fmea' | 'manual';
    productCategory: string;
    requirements: Requirement[];
    sourceId?: string;
    sourceName?: string;
    renderCad?: boolean;
}

export const MagnusGenesisWizard: React.FC<MagnusGenesisWizardProps> = ({
    isOpen,
    onClose,
    onStart,
    projects,
    pDiagrams,
    fmeas
}) => {
    const [inputSource, setInputSource] = useState<'project' | 'pdiagram' | 'fmea' | 'manual'>('manual');
    const [selectedProject, setSelectedProject] = useState<string>('');
    const [selectedPDiagram, setSelectedPDiagram] = useState<string>('');
    const [selectedFMEA, setSelectedFMEA] = useState<string>('');
    const [productCategory, setProductCategory] = useState<string>('');
    const [requirements, setRequirements] = useState<Requirement[]>([
        { requirementText: '' }
    ]);
    const [renderCad, setRenderCad] = useState<boolean>(true);

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            setInputSource('manual');
            setSelectedProject('');
            setSelectedPDiagram('');
            setSelectedFMEA('');
            setProductCategory('');
            setRequirements([{ requirementText: '' }]);
            setRenderCad(true);
        }
    }, [isOpen]);

    // Load data when source selection changes
    useEffect(() => {
        if (inputSource === 'project' && selectedProject) {
            const project = projects.find(p => p.name === selectedProject);
            if (project) {
                setProductCategory(project.name);
                setRequirements(project.requirements || [{ requirementText: '' }]);
            }
        } else if (inputSource === 'pdiagram' && selectedPDiagram) {
            const pDiagram = pDiagrams.find(pd => pd.id === selectedPDiagram);
            if (pDiagram) {
                setProductCategory(pDiagram.systemName || pDiagram.name);
                setRequirements(
                    (pDiagram.requirements || []).map(req => ({ requirementText: req }))
                );
            }
        } else if (inputSource === 'fmea' && selectedFMEA) {
            const fmea = fmeas.find(f => f.id === selectedFMEA);
            if (fmea) {
                setProductCategory(fmea.projectName);
                // Requirements would be loaded from FMEA context
                setRequirements([{ requirementText: `Expand analysis for ${fmea.item}` }]);
            }
        }
    }, [inputSource, selectedProject, selectedPDiagram, selectedFMEA, projects, pDiagrams, fmeas]);

    const addRequirement = () => {
        setRequirements([...requirements, { requirementText: '' }]);
    };

    const removeRequirement = (index: number) => {
        setRequirements(requirements.filter((_, i) => i !== index));
    };

    const updateRequirement = (index: number, value: string) => {
        const updated = [...requirements];
        updated[index] = { requirementText: value };
        setRequirements(updated);
    };

    const handleStart = () => {
        const config: AnalysisConfig = {
            inputSource,
            productCategory,
            requirements: requirements.filter(r => r.requirementText.trim() !== ''),
            sourceId:
                inputSource === 'project' ? selectedProject :
                    inputSource === 'pdiagram' ? selectedPDiagram :
                        inputSource === 'fmea' ? selectedFMEA :
                            undefined,
            sourceName:
                inputSource === 'project' ? selectedProject :
                    inputSource === 'pdiagram' ? pDiagrams.find(pd => pd.id === selectedPDiagram)?.name :
                        inputSource === 'fmea' ? fmeas.find(f => f.id === selectedFMEA)?.projectName :
                            productCategory,
            renderCad
        };

        onStart(config);
    };

    const isValid = () => {
        if (!productCategory.trim()) return false;
        if (requirements.filter(r => r.requirementText.trim() !== '').length === 0) return false;

        if (inputSource === 'project' && !selectedProject) return false;
        if (inputSource === 'pdiagram' && !selectedPDiagram) return false;
        if (inputSource === 'fmea' && !selectedFMEA) return false;

        return true;
    };

    if (!isOpen) return null;

    return (
        <div className="wizard-overlay" onClick={onClose}>
            <div className="wizard-modal" onClick={(e) => e.stopPropagation()}>
                <div className="wizard-header">
                    <h2>🧬 Magnus Genesis - Start New Analysis</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <div className="wizard-body">
                    <div className="wizard-section">
                        <h3>How would you like to start?</h3>

                        <div className="input-source-options">
                            {/* Option 1: From Existing Project */}
                            <label className={`source-option ${inputSource === 'project' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="inputSource"
                                    value="project"
                                    checked={inputSource === 'project'}
                                    onChange={(e) => setInputSource(e.target.value as any)}
                                />
                                <div className="option-content">
                                    <div className="option-icon">📁</div>
                                    <div className="option-details">
                                        <strong>From Existing Project</strong>
                                        <p>Continue analysis of an existing project</p>
                                    </div>
                                </div>
                            </label>

                            {inputSource === 'project' && (
                                <div className="source-details">
                                    <label>Select Project to Continue Analysis:</label>
                                    <div className="project-grid-compact">
                                        {projects.length > 0 ? (
                                            projects.map((project) => (
                                                <div 
                                                    key={project.name} 
                                                    onClick={() => setSelectedProject(project.name)}
                                                    className={`project-card-mini ${selectedProject === project.name ? 'selected' : ''}`}
                                                >
                                                    <div className="project-card-header">
                                                        <strong>{project.name}</strong>
                                                        {selectedProject === project.name && <span className="check-mark">✓</span>}
                                                    </div>
                                                    <p className="project-desc-mini">{project.description || 'No description available for this mechatronic project.'}</p>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="no-data-message">No projects available. Create one first or use manual entry.</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Option 2: From P-Diagram */}
                            <label className={`source-option ${inputSource === 'pdiagram' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="inputSource"
                                    value="pdiagram"
                                    checked={inputSource === 'pdiagram'}
                                    onChange={(e) => setInputSource(e.target.value as any)}
                                />
                                <div className="option-content">
                                    <div className="option-icon">📊</div>
                                    <div className="option-details">
                                        <strong>From P-Diagram</strong>
                                        <p>Generate FMEA from P-Diagram analysis</p>
                                    </div>
                                </div>
                            </label>

                            {inputSource === 'pdiagram' && (
                                <div className="source-details">
                                    <label>Select P-Diagram:</label>
                                    <select
                                        value={selectedPDiagram}
                                        onChange={(e) => setSelectedPDiagram(e.target.value)}
                                        className="wizard-select"
                                    >
                                        <option value="">-- Select a P-Diagram --</option>
                                        {pDiagrams.map((pd) => (
                                            <option key={pd.id} value={pd.id}>
                                                {pd.name}
                                            </option>
                                        ))}
                                    </select>
                                    {pDiagrams.length === 0 && (
                                        <p className="no-data-message">No P-Diagrams available. Create one first or use manual entry.</p>
                                    )}
                                </div>
                            )}

                            {/* Option 3: From Existing FMEA */}
                            <label className={`source-option ${inputSource === 'fmea' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="inputSource"
                                    value="fmea"
                                    checked={inputSource === 'fmea'}
                                    onChange={(e) => setInputSource(e.target.value as any)}
                                />
                                <div className="option-content">
                                    <div className="option-icon">📋</div>
                                    <div className="option-details">
                                        <strong>From Existing FMEA</strong>
                                        <p>Expand or refine existing FMEA analysis</p>
                                    </div>
                                </div>
                            </label>

                            {inputSource === 'fmea' && (
                                <div className="source-details">
                                    <label>Select FMEA:</label>
                                    <select
                                        value={selectedFMEA}
                                        onChange={(e) => setSelectedFMEA(e.target.value)}
                                        className="wizard-select"
                                    >
                                        <option value="">-- Select an FMEA --</option>
                                        {fmeas.map((fmea) => (
                                            <option key={fmea.id} value={fmea.id}>
                                                {fmea.projectName} - {fmea.item}
                                            </option>
                                        ))}
                                    </select>
                                    {fmeas.length === 0 && (
                                        <p className="no-data-message">No FMEAs available. Create one first or use manual entry.</p>
                                    )}
                                </div>
                            )}

                            {/* Option 4: Manual Entry */}
                            <label className={`source-option ${inputSource === 'manual' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="inputSource"
                                    value="manual"
                                    checked={inputSource === 'manual'}
                                    onChange={(e) => setInputSource(e.target.value as any)}
                                />
                                <div className="option-content">
                                    <div className="option-icon">✏️</div>
                                    <div className="option-details">
                                        <strong>Manual Entry (New Product)</strong>
                                        <p>Start fresh with a new product analysis</p>
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Product Details Section */}
                    <div className="wizard-section">
                        <h3>Product Details</h3>

                        <div className="form-group">
                            <label>Product Category / Name:</label>
                            <input
                                type="text"
                                value={productCategory}
                                onChange={(e) => setProductCategory(e.target.value)}
                                placeholder="e.g., Drone 3 propellers, Paint mixer, etc."
                                className="wizard-input"
                                disabled={inputSource !== 'manual'}
                            />
                        </div>

                        <div className="form-group">
                            <label>Requirements:</label>
                            {requirements.map((req, index) => (
                                <div key={index} className="requirement-row">
                                    <input
                                        type="text"
                                        value={req.requirementText}
                                        onChange={(e) => updateRequirement(index, e.target.value)}
                                        placeholder={`Requirement ${index + 1}`}
                                        className="wizard-input"
                                    />
                                    {requirements.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeRequirement(index)}
                                            className="remove-btn"
                                            title="Remove requirement"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={addRequirement}
                                className="add-requirement-btn"
                            >
                                + Add Requirement
                            </button>
                        </div>

                        <div className="form-group" style={{ marginTop: '20px', backgroundColor: 'rgba(59, 130, 246, 0.05)', padding: '15px', borderRadius: '8px', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: 0 }}>
                                <input
                                    type="checkbox"
                                    checked={renderCad}
                                    onChange={(e) => setRenderCad(e.target.checked)}
                                    style={{ width: '18px', height: '18px', accentColor: '#3b82f6', cursor: 'pointer' }}
                                />
                                <strong>Render 3D CAD (Mechatronic Assembly)</strong>
                            </label>
                            <p style={{ margin: '5px 0 0 28px', fontSize: '0.85rem', color: '#94a3b8' }}>
                                Generates disaggregated components and exact physical constraints. Skip to bypass M×N complexity and accelerate evolutionary synthesis.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="wizard-footer">
                    <button onClick={onClose} className="cancel-btn">
                        Cancel
                    </button>
                    <button
                        onClick={handleStart}
                        className="start-btn"
                        disabled={!isValid()}
                    >
                        Start Analysis
                    </button>
                </div>
            </div>
        </div>
    );
};
